import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, ArrowRight, Store, Repeat, Package, Briefcase, Coins, Sparkles, Leaf, BadgeCheck } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useRefData } from "@/context/RefData";
import { ListingCard, ListingSkeleton } from "@/components/ListingCard";
import { CategoryIcon, catLabel } from "@/components/CategoryIcon";
import { AiSearch } from "@/components/AiAssistant";

const useHomeCms = () => useQuery({ queryKey: ["cms-home"], queryFn: async () => (await api.get("/cms/home")).data, staleTime: 60000 }).data;

const Hero = () => {
  const { t } = useI18n();
  const { cities } = useRefData();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const cms = useHomeCms();
  const side = (cms?.side || []).filter(Boolean);
  const go = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) { p.set("city", city); p.set("sort", "distance"); }
    nav(`/cerca?${p.toString()}`);
  };
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#EFE9FB] via-[#F6F2FD] to-[#FFF0EE]" data-testid="home-hero">
      {cms?.background && <>
        <img src={imgSrc(cms.background)} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="home-hero-bg" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F6F2FD] via-[#F6F2FD]/85 to-[#F6F2FD]/10" />
      </>}
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:py-16">
        <div className="fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B1272]">{t("hero.eyebrow")}</p>
          <h1 className="mt-4 text-4xl font-extrabold leading-[1.02] text-[#1E1B2E] sm:text-5xl lg:text-6xl">
            {t("hero.title1")}<br /><span className="text-[#3B1272]">{t("hero.title2")}</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-[#4B4763] md:text-lg">{t("hero.subtitle")}</p>
          <form onSubmit={go} className="mt-7 flex flex-col gap-2 rounded-2xl bg-white p-2 shadow-[0_12px_40px_-12px_rgba(59,18,114,0.25)] sm:flex-row sm:items-center">
            <label className="flex flex-1 items-center gap-2 px-3">
              <Search className="h-5 w-5 text-[#64748B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hero.searchPh")} data-testid="hero-search-input" className="w-full bg-transparent py-3 text-sm outline-none" />
            </label>
            <label className="flex items-center gap-2 border-t border-[#EEF0F6] px-3 sm:border-l sm:border-t-0">
              <MapPin className="h-5 w-5 text-[#3B1272]" />
              <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="hero-city-select" className="w-full bg-transparent py-3 text-sm font-semibold outline-none sm:w-36">
                <option value="">{t("hero.cityPh")}</option>
                {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <button type="submit" className="btn-violet !rounded-xl !px-8 !py-3" data-testid="hero-search-btn">{t("hero.search")}</button>
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-[#4B4763]">{t("hero.popular")}</span>
            {t("hero.chips").map((p) => (
              <Link key={p} to={`/cerca?q=${p}`} data-testid={`popular-${p}`} className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-[#3B1272] transition-colors hover:bg-white">{p}</Link>
            ))}
          </div>
        </div>
        {side.length > 0 && <div className="relative hidden lg:block">
          <div className={`grid gap-3 ${side.length > 1 ? "grid-cols-5 grid-rows-2" : ""}`}>
            {side.map((src, i) => <img key={i} src={imgSrc(src)} alt="" data-testid={`home-hero-side-${i}`}
              className={`w-full rounded-3xl object-cover ${i === 0 ? `${side.length > 1 ? "col-span-3 row-span-2" : ""} h-[400px] shadow-xl` : `col-span-2 shadow-lg ${side.length === 2 ? "row-span-2 h-[400px]" : "h-[194px]"}`}`} />)}
          </div>
          <div className="absolute -top-3 right-6 rotate-[-5deg] rounded-xl bg-white px-4 py-2 font-display text-lg font-bold italic text-[#FF7A6B] shadow-lg">{t("hero.tag")}</div>
        </div>}
      </div>
    </section>
  );
};

const Categories = () => {
  const { categories } = useRefData();
  const { lang } = useI18n();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
      <div className="no-scrollbar flex gap-3 overflow-x-auto pb-2" data-testid="home-categories">
        {categories.map((c) => (
          <Link key={c.id} to={`/cerca?category=${c.id}`} data-testid={`home-cat-${c.id}`} className="card-soft flex min-w-[180px] shrink-0 items-center gap-3 px-4 py-3.5 hover:-translate-y-0.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EFE9FB] text-[#3B1272]"><CategoryIcon name={c.icon} className="h-5 w-5" /></span>
            <span><span className="block whitespace-nowrap text-sm font-bold text-[#1E1B2E]">{catLabel(c, lang)}</span><span className="text-xs text-[#94A3B8]">{c.count}</span></span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Areas = () => {
  const { t } = useI18n();
  const areaImg = useHomeCms()?.areas || {};
  const items = [
    { k: "outlet", icon: Package, to: "/cerca?area=outlet", bg: "bg-[#3B1272] text-white", ic: "bg-white/15" },
    { k: "market", icon: Store, to: "/cerca?area=market", bg: "bg-white", ic: "bg-[#FFF0EE] text-[#FF7A6B]" },
    { k: "scambi", icon: Repeat, to: "/cerca?exchange=true", bg: "bg-white", ic: "bg-[#EFE9FB] text-[#581C9C]" },
    { k: "pro", icon: Briefcase, to: "/cerca?area=pro", bg: "bg-white", ic: "bg-sky-50 text-sky-600" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6" data-testid="home-areas">
      <h2 className="text-xl font-bold text-[#1E1B2E] sm:text-2xl">{t("areas.title")}</h2>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {items.map(({ k, icon: Icon, to, bg, ic }, i) => (
          <Link key={k} to={to} data-testid={`area-${k}`} style={{ animationDelay: `${i * 80}ms` }} className={`card-soft fade-up group overflow-hidden p-4 hover:-translate-y-1 sm:p-6 ${bg}`}>
            {areaImg[k] && <img src={imgSrc(areaImg[k])} alt="" className="-mx-4 -mt-4 mb-4 h-24 w-[calc(100%+2rem)] max-w-none object-cover sm:-mx-6 sm:-mt-6 sm:h-36 sm:w-[calc(100%+3rem)]" data-testid={`area-${k}-img`} />}
            <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${ic}`}><Icon className="h-5 w-5" /></span>
            <h3 className="mt-3 text-base font-bold sm:mt-5 sm:text-lg">{t(`areas.${k}.t`)}</h3>
            <p className={`mt-1.5 hidden text-sm sm:block ${k === "outlet" ? "text-white/80" : "text-[#64748B]"}`}>{t(`areas.${k}.d`)}</p>
            <ArrowRight className="mt-2 h-4 w-4 sm:mt-4 transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </section>
  );
};

const ListingRow = ({ title, params, testid, to, hideEmpty }) => {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["home", params], queryFn: async () => (await api.get(`/listings?${params}`)).data });
  if (hideEmpty && !isLoading && !data?.items?.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6" data-testid={testid}>
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#1E1B2E] sm:text-3xl">{title}</h2>
        <Link to={to} className="flex items-center gap-1 text-sm font-bold text-[#3B1272] hover:gap-2" data-testid={`${testid}-see-all`}>{t("home.seeAll")}<ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        {isLoading ? [...Array(4)].map((_, i) => <ListingSkeleton key={i} />) : data?.items.map((l) => <ListingCard key={l.listing_id} l={l} />)}
      </div>
    </section>
  );
};

const Discovery = () => {
  const { t } = useI18n();
  const { cities } = useRefData();
  const [city, setCity] = useState(() => localStorage.getItem("juppo_city") || "");
  const pickCity = (v) => { setCity(v); if (v) localStorage.setItem("juppo_city", v); else localStorage.removeItem("juppo_city"); };
  const qs = city ? `city=${encodeURIComponent(city)}&sort=distance` : "sort=newest";
  const { data, isLoading } = useQuery({ queryKey: ["home", "discovery", city], queryFn: async () => (await api.get(`/listings?page_size=8&${qs}`)).data });
  return (
    <section className="mx-auto grid max-w-7xl gap-6 px-4 pt-10 sm:px-6 lg:grid-cols-[1fr_380px]" data-testid="home-discovery">
      <div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-2xl font-bold text-[#1E1B2E] sm:text-3xl">{t("p4.home.discovery")}</h2>
          <div className="flex items-center gap-4">
            <label className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${city ? "bg-[#EFE9FB] text-[#3B1272]" : "bg-[#FFF0EE] text-[#C2410C]"}`}>
              <MapPin className="h-4 w-4" /><span className="hidden sm:inline">{t("p4.home.near")}</span>
              <select value={city} onChange={(e) => pickCity(e.target.value)} className="bg-transparent font-bold outline-none" data-testid="discovery-city-select">
                <option value="">{t("p4.home.pickCity")}</option>
                {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <Link to={`/cerca?${qs}`} className="flex items-center gap-1 text-sm font-bold text-[#3B1272] hover:gap-2" data-testid="home-discovery-see-all">{t("home.seeAll")}<ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4">
          {isLoading ? [...Array(8)].map((_, i) => <ListingSkeleton key={i} />) : data?.items.map((l) => <ListingCard key={l.listing_id} l={l} />)}
        </div>
      </div>
      <aside className="h-fit rounded-3xl bg-[#3B1272] p-6 text-white lg:sticky lg:top-24" data-testid="home-ai-block">
        <div className="flex items-center gap-3"><img src="/juppo-logo.png" alt="" className="h-12 w-12 rounded-xl bg-white object-cover" style={{ objectPosition: "14% 50%" }} />
          <div><h3 className="flex items-center gap-1.5 text-xl font-bold">{t("p4.home.aiHello")}<Sparkles className="h-5 w-5 text-[#FF7A6B]" /></h3><p className="text-sm text-white/75">{t("p4.home.aiSub")}</p></div></div>
        <AiSearch />
        <div className="mt-4 grid gap-2">
          <Link to="/cerca?deals=true" className="flex items-center justify-center gap-2 rounded-full border border-white/40 py-2.5 text-sm font-semibold hover:bg-white/10" data-testid="ai-find-deal-btn">{t("p4.home.findDeal")}</Link>
          <Link to="/vendi" className="flex items-center justify-center gap-2 rounded-full border border-white/40 py-2.5 text-sm font-semibold hover:bg-white/10" data-testid="ai-help-sell-btn">{t("p4.home.helpSell")}</Link>
        </div>
      </aside>
    </section>
  );
};

const CoinAndAi = () => {
  const { t } = useI18n();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6">
      <div className="card-soft relative overflow-hidden p-8" data-testid="home-coin-block">
        <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-amber-600"><Coins className="h-5 w-5" /></span><h3 className="text-xl font-bold">{t("home.coinTitle")}</h3></div>
        <p className="mt-4 max-w-xl text-[#4B4763]">{t("home.coinText")}</p>
        <p className="mt-3 inline-block rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">{t("home.coinExample")}</p>
        <p className="mt-3 text-xs text-[#94A3B8]">{t("home.coinNote")}</p>
      </div>
    </section>
  );
};

const Trust = () => {
  const { t } = useI18n();
  const items = [[MapPin, "trust1"], [Leaf, "trust2"], [BadgeCheck, "trust3"]];
  return (
    <section className="mx-auto mt-16 max-w-7xl px-4 sm:px-6">
      <div className="grid gap-6 rounded-3xl bg-[#EFE9FB] p-8 md:grid-cols-3">
        {items.map(([Icon, k]) => (
          <div key={k} className="flex gap-4"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#3B1272] text-white"><Icon className="h-5 w-5" /></span>
            <div><p className="font-display font-bold text-[#3B1272]">{t(`home.${k}`)}</p><p className="text-sm text-[#64748B]">{t(`home.${k}d`)}</p></div></div>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useI18n();
  return (
    <div data-testid="home-page">
      <Hero />
      <Areas />
      <Categories />
      <Discovery />
      <ListingRow title={t("p4.home.top")} params="promo=top&page_size=4" testid="home-top" to="/cerca?promo=top" hideEmpty />
      <ListingRow title={t("p4.home.deals")} params="deals=true&page_size=4" testid="home-deals" to="/cerca?deals=true" hideEmpty />
      <ListingRow title={t("p4.home.lastPiece")} params="last_piece=true&page_size=4" testid="home-last-piece" to="/cerca?last_piece=true" hideEmpty />
      <ListingRow title={t("home.outlet")} params="area=outlet&page_size=4" testid="home-outlet" to="/cerca?area=outlet" />
      <CoinAndAi />
      <Trust />
    </div>
  );
}
